import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  formatMinor,
  GST_RATES,
  lineWeight,
  priceLine,
  resolveTierSet,
  savingsPercentVsSmallestTier,
  shippingMinorForZone,
  toMajor,
  toMinor,
  type PriceTier,
  type Product,
  type ProductShipping,
  type SelectedOption,
} from '@eprintsmart/shared';
import {
  loadProduct,
  savePriceTiers,
  saveProduct,
  saveProductPrintSpec,
  saveProductShipping,
  validateTierLadder,
  type HydratedProduct,
} from '../services/catalog';
import { ProductImageManager } from '../components/ProductImageManager';

export function ProductEditor() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<HydratedProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [notFound, setNotFound] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const loaded = await loadProduct(id);
    if (!loaded) setNotFound(true);
    setData(loaded);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !data) {
    return <Alert severity="error">No product with id &ldquo;{id}&rdquo;.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h1">{data.product.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            /p/{data.product.slug}
          </Typography>
        </Box>
        <Chip
          label={data.product.status}
          color={data.product.status === 'ACTIVE' ? 'success' : 'default'}
          variant="outlined"
        />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Details" />
        <Tab label="Print spec" />
        <Tab label={`Price ladder (${data.priceTiers.length})`} />
        <Tab label={`Options (${data.optionGroups.length})`} />
        <Tab label={`Images (${data.product.imageIds?.length ?? 0})`} />
        <Tab label="Shipping" />
      </Tabs>

      {tab === 0 && <DetailsTab data={data} onSaved={refresh} />}
      {tab === 1 && <PrintSpecTab data={data} onSaved={refresh} />}
      {tab === 2 && <PriceLadderTab data={data} onSaved={refresh} />}
      {tab === 3 && <OptionsTab data={data} />}
      {tab === 4 && <ProductImageManager
                productId={data.product.id}
                productName={data.product.name}
                initialImageIds={data.product.imageIds ?? []}
                onSaved={refresh}
              />}
      {tab === 5 && <ShippingTab data={data} onSaved={refresh} />}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */

/**
 * What one unit weighs, which is what shipping is charged on (shipping.ts).
 *
 * Paper goods give a GSM and their weight follows the chosen size; everything
 * else gives grams per piece. The preview below prices the minimum order at the
 * default options to three zones, so a wrong figure shows up as a wrong rupee
 * amount rather than as a number nobody can judge.
 */
function ShippingTab({ data, onSaved }: { data: HydratedProduct; onSaved: () => void }) {
  const initial = data.product.shipping;
  const [mode, setMode] = useState<'gsm' | 'fixed'>(initial?.unitWeightGrams ? 'fixed' : 'gsm');
  const [gsm, setGsm] = useState(String(initial?.gsm ?? ''));
  const [grams, setGrams] = useState(String(initial?.unitWeightGrams ?? ''));
  const [volumetric, setVolumetric] = useState(String(initial?.volumetricGramsPerUnit ?? ''));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const draft: ProductShipping | null = useMemo(() => {
    const vol = Number(volumetric) > 0 ? { volumetricGramsPerUnit: Number(volumetric) } : {};
    if (mode === 'gsm' && Number(gsm) > 0) return { gsm: Number(gsm), source: 'manual', ...vol };
    if (mode === 'fixed' && Number(grams) > 0) return { unitWeightGrams: Number(grams), source: 'manual', ...vol };
    return null;
  }, [mode, gsm, grams, volumetric]);

  const defaults: SelectedOption[] = data.optionGroups
    .filter((g) => g.required && !g.quantityAxis && g.values.length > 0)
    .map((g) => {
      const v = g.values.find((x) => x.isDefault) ?? g.values[0]!;
      return { groupId: g.id, groupName: g.name, valueId: v.id, valueName: v.name };
    });
  const product = { ...data.product, shipping: draft ?? undefined, optionGroups: data.optionGroups };
  const weigh = (selected: SelectedOption[], qty: number) => lineWeight({
    product, selected, qty,
    sizeWmm: data.product.customSize?.baseWmm, sizeHmm: data.product.customSize?.baseHmm,
  });
  const minOrder = weigh(defaults, data.product.minQty);
  const sizeGroup = data.optionGroups.find((g) => g.values.some((v) => v.printSpecOverride?.trimWidthMm));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await saveProductShipping(data.product.id, draft);
      setSaved(true);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        {initial && (
          <Typography variant="body2" color="text.secondary">
            On file: {initial.gsm ? `${initial.gsm} gsm` : `${initial.unitWeightGrams} g per piece`}
            {initial.volumetricGramsPerUnit ? ` · billed by volume at ${initial.volumetricGramsPerUnit} g` : ''}
            {' '}({initial.source === 'manual' ? 'set by hand' : `estimated from ${initial.source}`})
          </Typography>
        )}
        <TextField
          label="Weighed by"
          select
          value={mode}
          onChange={(e) => { setMode(e.target.value as 'gsm' | 'fixed'); setSaved(false); }}
          sx={{ maxWidth: 360 }}
          helperText="Paper, card, film and flex weigh by GSM x size; everything else by the piece."
        >
          <MenuItem value="gsm">Paper weight (GSM) x size</MenuItem>
          <MenuItem value="fixed">Grams per piece</MenuItem>
        </TextField>
        <Stack direction="row" spacing={2}>
          {mode === 'gsm' ? (
            <TextField label="GSM" type="number" value={gsm} onChange={(e) => { setGsm(e.target.value); setSaved(false); }} sx={{ flex: 1 }}
              helperText="Grams per square metre. A stock option naming its own GSM overrides this." />
          ) : (
            <TextField label="Grams per piece" type="number" value={grams} onChange={(e) => { setGrams(e.target.value); setSaved(false); }} sx={{ flex: 1 }}
              helperText="Packed product, without the outer carton." />
          )}
          <TextField label="Volumetric grams per piece" type="number" value={volumetric}
            onChange={(e) => { setVolumetric(e.target.value); setSaved(false); }} sx={{ flex: 1 }}
            helperText="Bulky items only: box L x B x H (cm) / 4500, in grams. Blank if not bulky." />
        </Stack>

        {draft && (
          <>
            <Divider />
            <Typography variant="subtitle2">
              Minimum order ({data.product.minQty}) at default options: {(Math.max(minOrder.deadGrams, minOrder.volumetricGrams) / 1000).toFixed(2)} kg
              {' '}&middot; ships for {formatMinor(shippingMinorForZone([minOrder], 'S1'))} within the South,
              {' '}{formatMinor(shippingMinorForZone([minOrder], 'N1'))} to Delhi,
              {' '}{formatMinor(shippingMinorForZone([minOrder], 'NE'))} to the North-East
            </Typography>
            {sizeGroup && (
              <Table size="small" sx={{ maxWidth: 480 }}>
                <TableHead>
                  <TableRow><TableCell>{sizeGroup.name}</TableCell><TableCell align="right">One piece</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {sizeGroup.values.map((v) => {
                    const sel = defaults.map((d) => (d.groupId === sizeGroup.id ? { ...d, valueId: v.id, valueName: v.name } : d));
                    const w = weigh(sel, 1);
                    return (
                      <TableRow key={v.id}>
                        <TableCell>{v.name}</TableCell>
                        <TableCell align="right">{(w.deadGrams / 1.1).toFixed(1)} g</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" onClick={save} disabled={saving || !draft}>Save shipping weight</Button>
          {saved && <Typography color="success.main" variant="body2">Saved.</Typography>}
        </Stack>
      </Stack>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */

function DetailsTab({ data, onSaved }: { data: HydratedProduct; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<Product>>(data.product);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof Product>(key: K, value: Product[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveProduct(data.product.id, form);
      setSaved(true);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        <TextField label="Name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} fullWidth />

        <TextField
          label="Short description"
          value={form.shortDescription ?? ''}
          onChange={(e) => set('shortDescription', e.target.value)}
          fullWidth
          helperText="One line, shown on category tiles and used as the meta description."
        />

        <TextField
          label="Description"
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
          fullWidth
          multiline
          minRows={4}
        />

        <Stack direction="row" spacing={2}>
          <TextField
            label="Minimum quantity"
            type="number"
            value={form.minQty ?? 1}
            onChange={(e) => set('minQty', Number(e.target.value))}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Turnaround (days)"
            type="number"
            value={form.turnaroundDays ?? 1}
            onChange={(e) => set('turnaroundDays', Number(e.target.value))}
            sx={{ flex: 1 }}
            helperText="Production only — transit is added by pincode."
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <TextField
            label="HSN code"
            value={form.hsnCode ?? ''}
            onChange={(e) => set('hsnCode', e.target.value)}
            sx={{ flex: 1 }}
            helperText="4–8 digits, printed on the GST invoice."
          />
          <TextField
            label="GST rate"
            select
            value={form.gstRatePercent ?? 18}
            onChange={(e) => set('gstRatePercent', Number(e.target.value))}
            sx={{ flex: 1 }}
            helperText="Prices are MRP — this is carved out, not added."
          >
            {GST_RATES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}%
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Status"
            select
            value={form.status ?? 'DRAFT'}
            onChange={(e) => set('status', e.target.value as Product['status'])}
            sx={{ flex: 1 }}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </TextField>
        </Stack>

        <TextField
          label="Custom quantities"
          select
          value={form.allowCustomQty ? 'yes' : 'no'}
          onChange={(e) => set('allowCustomQty', e.target.value === 'yes')}
          sx={{ maxWidth: 320 }}
          helperText="If off, only the listed quantities can be ordered."
        >
          <MenuItem value="no">Listed quantities only</MenuItem>
          <MenuItem value="yes">Any quantity above the minimum</MenuItem>
        </TextField>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" onClick={save} disabled={saving}>
            Save changes
          </Button>
          {saved && <Typography color="success.main" variant="body2">Saved.</Typography>}
        </Stack>
      </Stack>
    </Paper>
  );
}


/**
 * Mockup choices, grouped so the two KINDS are not confusable.
 *
 * A silhouette draws an object behind the artboard. A presentation decorates
 * around it, for products where the artboard IS the product. Picking a
 * silhouette for a flat product draws an object that does not exist, which is
 * the mistake this grouping exists to make obvious at the point of choosing.
 */
const MOCKUP_GROUPS: { label: string; types: string[] }[] = [
  { label: 'Apparel', types: ['tee', 'polo', 'crew', 'hoodie', 'shirt', 'jacket', 'apron', 'cap'] },
  { label: 'Bags', types: ['tote', 'backpack'] },
  { label: 'Drinkware & desk', types: ['mug', 'bottle', 'notebook', 'mousepad'] },
  { label: 'Large format', types: ['standee', 'flag'] },
  { label: 'Flat products (no object drawn)', types: ['card', 'sheet', 'banner'] },
];

/** Said at the point of choosing, where it can still prevent a wrong pick. */
const MOCKUP_HELP: Record<string, string> = {
  standee: 'Draws a roll-up cassette base and foot. Not for hanging banners — they have neither.',
  notebook: 'A bound cover with a spine. A pad of paper is "sheet" instead.',
  card: 'A small stack behind the artboard. No object is drawn.',
  sheet: 'A single sheet behind the artboard. No object is drawn.',
  banner: 'Eyelets at the trim corners. No object is drawn.',
  cap: 'Front panel only — a cap prints small.',
  jacket: 'Breast position, not a full chest print.',
};

/* ------------------------------------------------------------------ */

function PrintSpecTab({ data, onSaved }: { data: HydratedProduct; onSaved: () => void }) {
  const [spec, setSpec] = useState(data.product.printSpec);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof typeof spec>(key: K, value: (typeof spec)[K]) =>
    setSpec((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      // Whole-map write, so clearing the mockup actually clears it.
      await saveProductPrintSpec(data.product.id, spec as unknown as Record<string, unknown>);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const bleedW = spec.trimWidthMm + spec.bleedMm * 2;
  const bleedH = spec.trimHeightMm + spec.bleedMm * 2;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3} direction={{ xs: 'column', md: 'row' }}>
        <Stack spacing={2} sx={{ flex: 1, maxWidth: 520 }}>
          <Alert severity="info">
            This spec drives artwork preflight and, later, the design editor&rsquo;s artboard.
            Changing it after orders exist will not alter those orders — each stores its own copy.
          </Alert>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Trim width (mm)"
              type="number"
              value={spec.trimWidthMm}
              onChange={(e) => set('trimWidthMm', Number(e.target.value))}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Trim height (mm)"
              type="number"
              value={spec.trimHeightMm}
              onChange={(e) => set('trimHeightMm', Number(e.target.value))}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Bleed (mm)"
              type="number"
              value={spec.bleedMm}
              onChange={(e) => set('bleedMm', Number(e.target.value))}
              sx={{ flex: 1 }}
              helperText="3 mm is standard."
            />
            <TextField
              label="Safe margin (mm)"
              type="number"
              value={spec.safeMarginMm}
              onChange={(e) => set('safeMarginMm', Number(e.target.value))}
              sx={{ flex: 1 }}
              helperText="Keep text inside this."
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Minimum DPI"
              type="number"
              value={spec.minDpi}
              onChange={(e) => set('minDpi', Number(e.target.value))}
              sx={{ flex: 1 }}
              helperText="300 for print, 100 for large format."
            />
            <TextField
              label="Colour mode"
              select
              value={spec.colorMode}
              onChange={(e) => set('colorMode', e.target.value as 'CMYK' | 'RGB')}
              sx={{ flex: 1 }}
            >
              <MenuItem value="CMYK">CMYK</MenuItem>
              <MenuItem value="RGB">RGB</MenuItem>
            </TextField>
            <TextField
              label="Sides"
              select
              value={spec.sides}
              onChange={(e) => set('sides', Number(e.target.value) as 1 | 2)}
              sx={{ flex: 1 }}
            >
              <MenuItem value={1}>Single</MenuItem>
              <MenuItem value={2}>Double</MenuItem>
            </TextField>
          </Stack>

          {/* Editor mockup. Script-only until now, which meant a wrong
              assignment needed a migration to correct — and two were wrong on
              the day these were assigned (banners given a standee base they do
              not have, notepads given a bound-notebook cover). */}
          <TextField
            label="Editor mockup"
            select
            value={spec.mockup?.type ?? ''}
            onChange={(e) =>
              set('mockup', e.target.value ? { type: e.target.value } : undefined)
            }
            helperText={
              spec.mockup?.type
                ? MOCKUP_HELP[spec.mockup.type] ?? 'Shown behind the artboard in the design editor.'
                : 'None. Pick one only if it is true of the product — a wrong object misinforms more than no object.'
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {MOCKUP_GROUPS.map((group) => [
              <ListSubheader key={group.label}>{group.label}</ListSubheader>,
              ...group.types.map((t) => (
                <MenuItem key={t} value={t} sx={{ pl: 3 }}>
                  {t}
                </MenuItem>
              )),
            ])}
          </TextField>

          <Button variant="contained" onClick={save} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Save print spec
          </Button>
        </Stack>

        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <SpecPreview spec={spec} bleedW={bleedW} bleedH={bleedH} />
        </Box>
      </Stack>
    </Paper>
  );
}

/** Scale drawing of the bleed box, trim line and safe area. */
function SpecPreview({
  spec,
  bleedW,
  bleedH,
}: {
  spec: { trimWidthMm: number; trimHeightMm: number; bleedMm: number; safeMarginMm: number };
  bleedW: number;
  bleedH: number;
}) {
  const maxPx = 260;
  const scale = maxPx / Math.max(bleedW, bleedH);
  const px = (mm: number) => mm * scale;

  return (
    <Stack alignItems="center" spacing={1}>
      <Box
        sx={{
          position: 'relative',
          width: px(bleedW),
          height: px(bleedH),
          bgcolor: 'error.light',
          opacity: 0.9,
          borderRadius: 0.5,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: `${px(spec.bleedMm)}px`,
            bgcolor: 'background.paper',
            outline: '1px solid',
            outlineColor: 'text.primary',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: `${px(spec.safeMarginMm)}px`,
              border: '1px dashed',
              borderColor: 'primary.main',
            }}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        Bleed {bleedW} × {bleedH} mm &middot; trim {spec.trimWidthMm} × {spec.trimHeightMm} mm
        <br />
        Dashed line is the safe area
      </Typography>
    </Stack>
  );
}

/* ------------------------------------------------------------------ */

function PriceLadderTab({ data, onSaved }: { data: HydratedProduct; onSaved: () => void }) {
  const [tiers, setTiers] = useState<PriceTier[]>(data.priceTiers);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const problems = useMemo(() => validateTierLadder(tiers), [tiers]);
  const defaultLadder = useMemo(
    () => [...tiers.filter((t) => t.optionKey === '*')].sort((a, b) => a.qty - b.qty),
    [tiers],
  );

  const update = (id: string, patch: Partial<PriceTier>) => {
    setTiers((list) => list.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    setSaved(false);
  };

  const addTier = () => {
    const last = defaultLadder.at(-1);
    const qty = last ? last.qty * 2 : data.product.minQty;
    setTiers((list) => [
      ...list,
      { id: `q${qty}_${Date.now()}`, qty, priceMinor: last?.priceMinor ?? 0, optionKey: '*' },
    ]);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await savePriceTiers(data.product.id, tiers);
      setSaved(true);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {problems.map((p, i) => (
        <Alert key={i} severity="warning">
          {p}
        </Alert>
      ))}

      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the <strong>total price for that quantity</strong>, inclusive of GST. The per-unit
          rate and savings badge are calculated for you.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={120}>Quantity</TableCell>
              <TableCell width={160}>Total price (₹)</TableCell>
              <TableCell align="right">Per unit</TableCell>
              <TableCell align="right">Saving</TableCell>
              <TableCell>Badge</TableCell>
              <TableCell width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {defaultLadder.map((tier) => (
              <TableRow key={tier.id}>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={tier.qty}
                    onChange={(e) => update(tier.id, { qty: Number(e.target.value) })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={toMajor(tier.priceMinor)}
                    onChange={(e) => update(tier.id, { priceMinor: toMinor(Number(e.target.value)) })}
                  />
                </TableCell>
                <TableCell align="right">
                  {tier.qty > 0 ? formatMinor(Math.round(tier.priceMinor / tier.qty)) : '—'}
                </TableCell>
                <TableCell align="right">
                  {savingsPercentVsSmallestTier(defaultLadder, tier) > 0
                    ? `${savingsPercentVsSmallestTier(defaultLadder, tier)}%`
                    : '—'}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="e.g. Most popular"
                    value={tier.badge ?? ''}
                    onChange={(e) => update(tier.id, { badge: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove tier">
                    <IconButton
                      size="small"
                      onClick={() => setTiers((l) => l.filter((t) => t.id !== tier.id))}
                    >
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Button startIcon={<AddIcon />} onClick={addTier}>
            Add tier
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || problems.length > 0}>
            Save ladder
          </Button>
          {saved && <Typography color="success.main" variant="body2">Saved.</Typography>}
          {problems.length > 0 && (
            <Typography color="warning.main" variant="body2">
              Fix the warnings above before saving.
            </Typography>
          )}
        </Stack>
      </Paper>

      <LivePriceCheck data={{ ...data, priceTiers: tiers }} />
    </Stack>
  );
}

/**
 * Runs the real pricing engine over the edited ladder so a mistake shows up
 * here rather than on a live product page.
 */
function LivePriceCheck({ data }: { data: HydratedProduct }) {
  const selected = data.optionGroups
    .filter((g) => g.required)
    .map((g) => {
      const value = g.values.find((v) => v.isDefault) ?? g.values[0];
      return {
        groupId: g.id,
        groupName: g.name,
        valueId: value?.id ?? '',
        valueName: value?.name ?? '',
      };
    });

  const ladder = resolveTierSet(data.priceTiers, selected);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        What the customer will see
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={0.5}>
        {ladder.map((tier) => {
          let result: string;
          try {
            const price = priceLine({
              product: data.product,
              optionGroups: data.optionGroups,
              selected,
              tiers: data.priceTiers,
              qty: tier.qty,
            });
            result = `${formatMinor(price.subtotalMinor)} (${formatMinor(price.unitPriceMinor)} each)`;
          } catch (err) {
            result = err instanceof Error ? err.message : 'Pricing failed';
          }
          return (
            <Typography key={tier.id} variant="body2">
              {tier.qty} &rarr; {result}
            </Typography>
          );
        })}
        {ladder.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No ladder applies to the default option selection.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */

function OptionsTab({ data }: { data: HydratedProduct }) {
  if (data.optionGroups.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">This product has no options.</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {data.optionGroups.map((group) => (
        <Paper key={group.id} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {group.name}
            </Typography>
            <Chip size="small" variant="outlined" label={group.inputType} />
            <Chip
              size="small"
              variant="outlined"
              label={group.stage === 'preDesign' ? 'before design' : 'after design'}
              color={group.stage === 'preDesign' ? 'default' : 'primary'}
            />
            {group.required && <Chip size="small" label="required" variant="outlined" />}
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Value</TableCell>
                <TableCell>Charge</TableCell>
                <TableCell>Sales copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.values.map((value) => (
                <TableRow key={value.id}>
                  <TableCell>
                    {value.name}
                    {value.isDefault && (
                      <Chip size="small" label="default" sx={{ ml: 1 }} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {value.modifierValue === 0
                      ? 'Included'
                      : value.modifierType === 'percent'
                        ? `+${value.modifierValue}%`
                        : value.modifierType === 'perUnit'
                          ? `+${formatMinor(value.modifierValue)} each`
                          : `+${formatMinor(value.modifierValue)}`}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {value.bestFor ? `Best for: ${value.bestFor}` : value.description ?? '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}
    </Stack>
  );
}
